import { useEffect, useState } from "react";
import { getBrowserLanguage, translations } from "./translations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGithub, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import { faArrowUpRightFromSquare, faDownload, faEnvelope, faLocationArrow } from "@fortawesome/free-solid-svg-icons";
import "./components/icons/Icons";
import projects from "./data/projects.json";

const { featured: featuredProjects, more: moreProjects } = projects;

function App() {
    const [language, setLanguage] = useState(getBrowserLanguage);
    const copy = translations[language];

    useEffect(() => {
        document.documentElement.lang = language;
    }, [language]);

    return (
        <main className="min-h-screen overflow-x-hidden bg-[#030503] text-white">
            <nav className="sticky top-0 z-30 border-b border-white/10 bg-[#030503]/85 backdrop-blur">
                <div className="mx-auto flex max-w-6xl items-center justify-end gap-8 px-5 py-3">
                    <div className="hidden items-center gap-8 text-sm text-white/70 md:flex">
                        <a href="#resume" className="transition hover:text-lime-300">{copy.resume}</a>
                        <a href="#projects" className="transition hover:text-lime-300">{copy.projects}</a>
                        <a href="#contact" className="transition hover:text-lime-300">{copy.contact}</a>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-white/70">
                        <span>{copy.language}</span>
                        <select
                            value={language}
                            onChange={(event) => setLanguage(event.target.value)}
                            className="min-h-[44px] rounded-md border border-white/20 bg-[#030503] px-3 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-300"
                        >
                            <option value="en" lang="en">English</option>
                            <option value="es" lang="es">Español</option>
                        </select>
                    </label>
                </div>
            </nav>

            <section id="home" className="mx-auto grid max-w-6xl gap-8 px-5 py-12 md:grid-cols-[1.05fr_0.95fr] md:items-center md:gap-12 md:py-24">
                <div>
                    <div className="flex items-center gap-4 md:block">
                        <div className="min-w-0 flex-1">
                            <h1 className="max-w-3xl text-4xl font-semibold leading-tight sm:text-5xl md:text-7xl">
                                Luis Arango
                            </h1>
                            <p className="mt-3 text-xl font-medium text-lime-200 sm:text-2xl md:text-3xl">
                                {copy.role}
                            </p>
                        </div>
                        <img src="/assets/images/symbol.svg" width="138" height="432" className="h-28 w-auto shrink-0 md:hidden" alt="" />
                    </div>
                    <p className="mt-6 max-w-2xl text-lg leading-8 text-white/72">
                        {copy.intro}
                    </p>
                    <div className="mt-7 flex flex-wrap gap-3">
                        <a className="inline-flex w-full items-center justify-center gap-3 rounded-md border border-lime-300/60 bg-lime-300 px-5 py-3 text-sm font-semibold text-[#111707] transition hover:bg-lime-200 sm:w-auto" href="mailto:luis.arango@ileaf.co.nz">
                            <FontAwesomeIcon icon={faEnvelope} />
                            {copy.email}
                        </a>
                        <a className="inline-flex flex-1 items-center justify-center gap-3 rounded-md border border-white/15 px-5 py-3 text-sm font-semibold text-white/85 transition hover:border-lime-300/60 hover:text-lime-200 sm:flex-none" href="https://www.linkedin.com/in/learangof/" target="_blank" rel="noreferrer">
                            <FontAwesomeIcon icon={faLinkedin} />
                            LinkedIn
                        </a>
                        <a className="inline-flex flex-1 items-center justify-center gap-3 rounded-md border border-white/15 px-5 py-3 text-sm font-semibold text-white/85 transition hover:border-lime-300/60 hover:text-lime-200 sm:flex-none" href="https://github.com/learangof" target="_blank" rel="noreferrer">
                            <FontAwesomeIcon icon={faGithub} />
                            GitHub
                        </a>
                    </div>
                </div>

                <div className="mx-auto hidden aspect-square w-full max-w-[430px] items-center justify-center md:flex">
                    <img src="/assets/images/symbol.svg" width="138" height="432" className="h-4/5 w-4/5 object-contain" alt={copy.symbol} />
                </div>
            </section>

            <section id="resume" className="border-y border-white/10 bg-white/[0.03]">
                <div className="mx-auto grid max-w-6xl gap-10 px-5 py-16 md:grid-cols-[0.85fr_1.15fr]">
                    <div>
                        <h2 className="text-3xl font-semibold md:text-4xl">{copy.briefResume}</h2>
                        <p className="mt-5 text-base leading-8 text-white/70">
                            {copy.background}
                        </p>
                        <a className="mt-6 inline-flex items-center gap-3 rounded-md border border-lime-300/50 px-4 py-3 text-sm font-semibold text-lime-200 transition hover:bg-lime-300 hover:text-[#111707]" href="/assets/documents/Luis-Arango-CV.pdf" download>
                            <FontAwesomeIcon icon={faDownload} aria-hidden="true" />
                            {copy.download}
                        </a>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        {copy.resumeFacts.map(([label, value]) => (
                            <div key={label} className="rounded-lg border border-white/10 bg-black/25 p-5">
                                <p className="text-sm font-semibold text-lime-200">{label}</p>
                                <p className="mt-3 text-sm leading-6 text-white/72">{value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section id="projects" className="mx-auto max-w-6xl px-5 py-16 md:py-20">
                <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                    <div>
                        <h2 className="text-3xl font-semibold md:text-4xl">{copy.personalProjects}</h2>
                        <p className="mt-4 max-w-2xl text-white/68">
                            {copy.projectsIntro}
                        </p>
                    </div>
                    <a className="inline-flex w-fit items-center gap-2 rounded-md border border-lime-300/50 px-4 py-2 text-sm font-semibold text-lime-200 transition hover:bg-lime-300 hover:text-[#111707]" href="https://github.com/learangof" target="_blank" rel="noreferrer">
                        {copy.github}
                        <FontAwesomeIcon icon={faGithub} />
                    </a>
                </div>

                <div className="mt-10 grid gap-4">
                    {featuredProjects.map((project) => (
                        <article key={project.name} className="grid gap-5 rounded-lg border border-white/10 bg-white/[0.035] p-5 transition hover:border-lime-300/45 md:grid-cols-[1fr_auto] md:items-center">
                            <div>
                                <div className="flex items-center gap-3">
                                    <img
                                        src={project.icon || "/assets/images/favicon.png"}
                                        onError={(event) => {
                                            event.currentTarget.onerror = null;
                                            event.currentTarget.src = "/assets/images/favicon.png";
                                        }}
                                        className="h-12 w-12 shrink-0 rounded-xl bg-[#f7f1e7] object-contain"
                                        alt=""
                                    />
                                    <h3 className="text-xl font-semibold">
                                        {project.url ? (
                                            <a href={project.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-lime-200 transition hover:text-lime-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-lime-300">
                                                {project.name}
                                            </a>
                                        ) : project.name}
                                    </h3>
                                    {project.url && (
                                        <a href={project.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-white/55 underline decoration-white/30 underline-offset-4 transition hover:text-lime-200">
                                            {new URL(project.url).host}
                                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} aria-hidden="true" />
                                        </a>
                                    )}
                                </div>
                                {project.summary && <p className="mt-3 max-w-3xl text-sm leading-7 text-white/68">{copy.projectSummaries?.[project.name] || project.summary}</p>}
                            </div>
                            {project.tags?.length > 0 && (
                                <div className="flex flex-wrap gap-2 md:max-w-xs md:justify-end">
                                    {project.tags.map((tag) => (
                                        <span key={tag} className="rounded-md border border-lime-300/25 px-3 py-1 text-xs text-lime-100/85">
                                            {copy.tags?.[tag] || tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </article>
                    ))}
                </div>

                <div className="mt-12">
                    <h3 className="text-xl font-semibold">{copy.moreProjects}</h3>
                    <div className="mt-5 flex flex-wrap gap-3">
                        {moreProjects.map((project) => {
                            const isLink = Array.isArray(project);
                            const name = isLink ? project[0] : project;
                            const className = isLink
                                ? "rounded-md border border-lime-300/25 bg-lime-300/10 px-3 py-2 text-sm text-lime-200 underline decoration-lime-300/70 underline-offset-4 transition hover:text-lime-100 hover:decoration-lime-200"
                                : "rounded-md border border-white/10 bg-white/[0.035] px-3 py-2 text-sm text-white/65";

                            return isLink ? (
                                <a key={name} href={project[1]} target="_blank" rel="noreferrer" className={className}>
                                    {name}
                                </a>
                            ) : (
                                <span key={name} className={className}>{name}</span>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section id="contact" className="mx-auto max-w-6xl px-5 pb-16">
                <div className="rounded-lg border border-lime-300/25 bg-lime-300/[0.08] p-7 md:flex md:items-center md:justify-between">
                    <div>
                        <h2 className="text-2xl font-semibold">{copy.letsTalk}</h2>
                        <p className="mt-3 text-white/68">{copy.contactIntro}</p>
                    </div>
                    <a className="mt-6 inline-flex items-center gap-3 rounded-md bg-lime-300 px-5 py-3 text-sm font-semibold text-[#111707] transition hover:bg-lime-200 md:mt-0" href="mailto:luis.arango@ileaf.co.nz">
                        <FontAwesomeIcon icon={faLocationArrow} />
                        {copy.getInTouch}
                    </a>
                </div>
            </section>
        </main>
    );
}

export default App;
